import { FunctionComponent, useMemo } from "react";
import { formatDistance, formatDistanceForLegs } from "@lib/calculateDistance";
import { formatDuration, formatDurationForLegs } from "@lib/calculateDuration";
import { PlanItem } from "@dto/PlanItem";
import { Direction } from "@dto/Direction";
import { ItemDirection } from "@domain/ItemDirection";

import "./StopSummary.css";
import { isNil } from "@lib/isNil";

interface StopSummaryProps {
  /**
   * Plan items of the parent Tour or Plan.
   */
  planItems: PlanItem[];
  /**
   * Whether to show duration and distance or not.
   */
  routingEnabled: boolean;
  /**
   * Directions data for the parent Tour, necessary to show duration and distance.
   * Google Directions API based.
   */
  directions?: Direction | null | undefined;
  /**
   * Directions data for the parent Plan, necessary to show duration and distance.
   * Google Routes API based.
   */
  itemDirections?: ItemDirection[] | null | undefined;
}

const StopSummary: FunctionComponent<StopSummaryProps> = ({
  planItems,
  routingEnabled: _routingEnabled,
  directions,
  itemDirections,
}) => {
  const legs = directions?.routes[0]?.legs;
  const totalDistanceInMeters = itemDirections?.reduce((acc, item) => {
    return acc + item.distanceInMeters;
  }, 0);

  const totalDurationInSeconds = itemDirections?.reduce((acc, item) => {
    return acc + item.timeInSeconds;
  }, 0);

  const { distanceFormatted, unit: distanceUnit } = useMemo(() => {
    if (!isNil(totalDistanceInMeters)) {
      return formatDistance(totalDistanceInMeters!);
    }

    return formatDistanceForLegs(legs);
  }, [legs, totalDistanceInMeters]);

  const { durationFormatted, unit: durationUnit } = useMemo(() => {
    if (!isNil(totalDurationInSeconds)) {
      return formatDuration(totalDurationInSeconds!);
    }

    return formatDurationForLegs(legs);
  }, [legs, totalDurationInSeconds]);

  const showTotals =
    !isNil(totalDistanceInMeters) && !isNil(totalDurationInSeconds);

  return (
    <div className="tour-routing-component">
      <div className="group">
        <div className="value">{planItems.length}</div>
        <div className="label">stops</div>
      </div>
      {showTotals && (
        <>
          <div className="group">
            <div className="value">{durationFormatted}</div>
            <div className="label">{durationUnit}</div>
          </div>
          <div className="group">
            <div className="value">{distanceFormatted}</div>
            <div className="label">{distanceUnit}</div>
          </div>
        </>
      )}
    </div>
  );
};

export { StopSummary };
